/**
 * Session-only "view as" role for super admins — does not change profiles.role in the DB.
 * On serviceapp.ai, keys are namespaced so T&B role previews do not affect SaaS login.
 */

import { getAppMode } from '../saas/appMode.js';
import { isPlatformSuperAdminEmail, resolvePlatformOwnerDashboardRole } from './platformSuperAdminEmail.js';

export interface RoleViewOverride {
  originalRole: string;
  targetRole: string;
  switchedAt: string;
}

function scopeSuffix(): string {
  return getAppMode() === 'saas-platform' ? '_saas' : '';
}

const keys = (userId: string) => {
  const suffix = scopeSuffix();
  return {
    meta: `role_switch_meta_${userId}${suffix}`,
    original: `original_role_${userId}${suffix}`,
    flag: `role_switched_${userId}${suffix}`,
  };
};

/** Legacy T&B keys (no _saas suffix) — cleared on SaaS login. */
const legacyKeys = (userId: string) => ({
  meta: `role_switch_meta_${userId}`,
  original: `original_role_${userId}`,
  flag: `role_switched_${userId}`,
});

export function getRoleViewOverride(userId: string): RoleViewOverride | null {
  if (!userId) return null;
  try {
    const { meta } = keys(userId);
    const raw = localStorage.getItem(meta);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as RoleViewOverride;
    if (parsed?.originalRole && parsed?.targetRole) return parsed;
  } catch {
    /* ignore */
  }
  return null;
}

export function setRoleViewOverride(userId: string, originalRole: string, targetRole: string): void {
  const { meta, original, flag } = keys(userId);
  const payload: RoleViewOverride = {
    originalRole,
    targetRole,
    switchedAt: new Date().toISOString(),
  };
  localStorage.setItem(meta, JSON.stringify(payload));
  localStorage.setItem(original, originalRole);
  localStorage.setItem(flag, 'true');
}

export function clearRoleViewOverride(userId: string): void {
  const scoped = keys(userId);
  localStorage.removeItem(scoped.meta);
  localStorage.removeItem(scoped.original);
  localStorage.removeItem(scoped.flag);

  const legacy = legacyKeys(userId);
  localStorage.removeItem(legacy.meta);
  localStorage.removeItem(legacy.original);
  localStorage.removeItem(legacy.flag);
}

/** Remove every role-preview key for this user (legacy + suffixed variants). */
export function clearAllRoleViewOverridesForUser(userId: string): void {
  if (!userId || typeof localStorage === 'undefined') return;
  clearRoleViewOverride(userId);
  const prefixes = ['role_switch_meta_', 'original_role_', 'role_switched_'];
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key || !key.includes(userId)) continue;
    if (prefixes.some((p) => key.startsWith(p))) keysToRemove.push(key);
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key));
}

/** Base role stored when starting a dashboard preview (always super_admin for org owners). */
export function previewOriginalRole(dbRole: string, orgMemberRole?: string | null): string {
  if (dbRole === 'super_admin' || orgMemberRole === 'super_admin') {
    return 'super_admin';
  }
  return dbRole;
}

const PLATFORM_OWNER_DASHBOARD_ROLES = new Set(['super_admin', 'admin', 'supervisor', 'customer']);

/** Persist super admin dashboard for platform owner on T&B (default dashboard is admin). */
export function restorePlatformOwnerSuperAdminDashboard(userId: string): void {
  const defaultRole = resolvePlatformOwnerDashboardRole();
  if (defaultRole === 'super_admin') {
    clearRoleViewOverride(userId);
    return;
  }
  setRoleViewOverride(userId, defaultRole, 'super_admin');
}

/** Apply view-as override when the account is (or was previewing as) super admin. */
export function resolveEffectiveRole(userId: string, dbRole: string, email?: string | null): string {
  const override = getRoleViewOverride(userId);

  if (email && isPlatformSuperAdminEmail(email)) {
    if (override?.targetRole && PLATFORM_OWNER_DASHBOARD_ROLES.has(override.targetRole)) {
      return override.targetRole;
    }
    if (override?.targetRole) {
      clearRoleViewOverride(userId);
    }
    return resolvePlatformOwnerDashboardRole();
  }

  if (!override?.targetRole) return dbRole;

  const canApply = dbRole === 'super_admin' || override.originalRole === 'super_admin';

  if (canApply) {
    return override.targetRole;
  }

  clearRoleViewOverride(userId);
  return dbRole;
}

export function hasActiveRoleViewOverride(userId: string): boolean {
  return getRoleViewOverride(userId) != null;
}
